// useEventFilter — generic time-window filter for event lists.
// Pass an array of events (with `occurred_at` Firestore Timestamps or Date-likes)
// and a window key; receive the filtered subset memoised by reference.
//
// Window keys:
//   '1h'  — last 60 minutes
//   '24h' — last 24 hours        (default)
//   '7d'  — last 7 days
//   'all' — no time filter
//
// `toMs` is exposed so callers (TapeStatsBar, RecipientTimeline) can reuse
// the same Timestamp coercion without reaching into Firestore types directly.

import { useMemo } from 'react';

export const WINDOW_OPTIONS = [
  { id: '1h',  label: 'Last hour', ms: 60 * 60 * 1000 },
  { id: '24h', label: 'Last 24h',  ms: 24 * 60 * 60 * 1000 },
  { id: '7d',  label: 'Last 7d',   ms: 7 * 24 * 60 * 60 * 1000 },
  { id: 'all', label: 'All',       ms: null },
];

export function toMs(ts) {
  if (!ts) return 0;
  if (typeof ts === 'number') return ts;
  if (ts.toMillis) return ts.toMillis();
  if (ts.seconds != null) return ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1e6);
  if (ts.toDate) return ts.toDate().getTime();
  if (ts instanceof Date) return ts.getTime();
  const n = Date.parse(ts);
  return Number.isNaN(n) ? 0 : n;
}

export function windowMs(windowKey) {
  const opt = WINDOW_OPTIONS.find((w) => w.id === windowKey);
  return opt ? opt.ms : null;
}

export function useEventFilter(events, windowKey = '24h', { now } = {}) {
  return useMemo(() => {
    const ms = windowMs(windowKey);
    if (!ms) return events || [];
    const cutoff = (now || Date.now()) - ms;
    return (events || []).filter((e) => toMs(e.occurred_at) >= cutoff);
  }, [events, windowKey, now]);
}
